<template>
  <div class="">
    <div v-if="waitingForSessionAttempt" class="alert alert-info my-3" role="alert" >Attaching to your session...</div>
    <div v-else-if="!sessionState" class="alert alert-warning my-3" role="alert" >Please log in as an administrator!</div>
    <div v-else-if="!isAdmin" class="alert alert-warning my-3" role="alert" >you are not an administrator!</div>
    <div v-else class="container">
      <div class="row gx-0 mb-2 py-2 bg-light rounded-1">
        <div class="col-12 d-flex align-items-center justify-content-end gap-2 fs-xs flex-wrap px-2">
          <span class="text-secondary fw-semibold">Window:</span>
          <div class="btn-group" role="group">
            <button v-for="opt in timeWindowOptions" :key="'tw'+opt.value"
              class="btn btn-sm" :class="historyFilterSecsAgo===opt.value ? 'btn-primary' : 'btn-outline-secondary'"
              @click="setTimeWindow(opt.value)">{{opt.label}}</button>
          </div>
          <span class="text-secondary fw-semibold ms-2">Aggregation:</span>
          <div class="btn-group" role="group">
            <button v-for="opt in aggregationOptions" :key="'agg'+opt.value"
              class="btn btn-sm" :class="chartBinSeconds===opt.value ? 'btn-primary' : 'btn-outline-secondary'"
              @click="setAggregation(opt.value)">{{opt.label}}</button>
          </div>
          <button class="btn btn-sm btn-outline-secondary ms-2" @click="openServerEditor()" title="Edit servers">⚙ Servers</button>
          <span class="ms-auto chart-legend">
            <span class="legend-item"><span class="legend-swatch legend-success"></span>Req/s</span>
            <span class="legend-item"><span class="legend-swatch legend-exectime"></span>Proc (ms)</span>
            <span class="legend-item"><span class="legend-swatch legend-p99exectime"></span>p99</span>
            <span class="legend-item"><span class="legend-swatch legend-totalexectime"></span>Total (ms)</span>
            <span class="legend-item"><span class="legend-swatch legend-unhandled"></span>Unhandled</span>
            <span class="legend-item"><span class="legend-swatch legend-handled"></span>Handled</span>
            <span class="legend-item"><span class="legend-swatch legend-license"></span>License</span>
          </span>
        </div>
      </div>

       <div v-for="server in servers" class="row fs-sm py-3 gx-0 border-bottom" :class="{'server-paused': server.paused, 'server-backoff': !server.paused && server.backoffUntil}">
        <div class="col-12 col-lg-1 text-center d-flex flex-row flex-lg-column align-items-center justify-content-center">
          <button class="btn btn-sm btn-link text-primary fw-semibold text-decoration-none" type="button" @click="openServerLogModal(server)">{{server.name}}</button>
          <button class="btn btn-sm p-0" type="button" @click="openServerStatusModal(server)">
            <span class="badge" :class="server.status==='OK' ? 'bg-success' : (server.status==='COMM_ERR' ? 'bg-danger' : 'bg-warning text-dark')">{{server.status}}</span>
          </button>
          <div class="fs-xs">
            <span class="text-danger fw-semibold">{{errorCount(server)}}</span> <span class="text-muted">Err</span>
            <span class="text-warning fw-semibold ms-1">{{warnCount(server)}}</span> <span class="text-muted">Warn</span>
          </div>
          <button class="btn btn-sm btn-link text-primary text-decoration-none p-0" type="button" @click="openServerJobViewerModal(server)">{{getActiveJobCount(server)}} Jobs</button>
          <div v-if="server.paused || server.backoffUntil" class="mt-1">
            <a v-if="!server.paused" href="#" class="badge bg-warning text-dark text-decoration-none fs-xxs" @click.prevent="resumeServer(server)" :title="'Attempt '+server.failCount+'/6 — click to retry now'">{{getServerBackoffLabel(server)}}</a>
            <span v-else class="badge bg-danger fs-xxs">Paused</span>
            <button v-if="server.paused" class="btn btn-sm btn-outline-warning py-0 px-1 fs-xxs ms-1" @click="resumeServer(server)">Resume</button>
          </div>
        </div>
       <div class="col-4 col-lg-1 text-center d-flex flex-column ">
          <div class="mt-auto">{{format(server.monitorData.freememorypercent,1,"%")}}</div>
          <div class="fs-xs">{{formatGb(server.monitorData.freeMemory)}}/{{formatGb(server.monitorData.totalMemory)}}Gb</div>
          <div class="fs-xxs text-muted mt-auto">Memory</div>
        </div>
        
        <div class="col-4 col-lg-1 text-center d-flex flex-column ">
          <template v-for="(min,i) in statisticsMins">
            <div v-if="server.monitorData.stats" :class="{'mt-auto':i===0}" class="d-flex justify-content-center gap-1 lh-1" style="font-size:11px;">
              <span class="text-danger fw-semibold" :title="'Unhandled'">{{formatCompact(server.monitorData.stats[min].unhandled)}}</span>
              <span class="text-warning" :title="'Handled'">{{formatCompact(server.monitorData.stats[min].handled)}}</span>
              <span class="text-info" :title="'License'">{{formatCompact(server.monitorData.stats[min].license)}}</span>
              <span class="text-secondary" style="font-size:10px;">{{format(server.monitorData.stats[min].errorRate,1,"%")}}</span>
            </div>
            <div class="ps-1 fs-xxs text-muted">{{min}} min</div>
          </template>
          <div class="fs-xxs text-muted mt-auto">Errors</div>
        </div>
        <div class="col-4 col-lg-1 text-center d-flex flex-column ">
           <template v-for="(min,i) in statisticsMins">
            <div v-if="server.monitorData.stats" :class="{'mt-auto':i===0}" style="font-size:11px;line-height:1.3">
              <span :title="'Mean processing'">{{format(server.monitorData.stats[min].meanTime,0,"")}}</span>
              <span class="text-muted" style="font-size:9px" :title="'p99 processing'">/{{format(server.monitorData.stats[min].p99ExecTime,0,"")}}</span>
              <span style="color:#3366cc;font-size:10px" :title="'Mean total'">/{{format(server.monitorData.stats[min].meanTotalTime,0,"ms")}}</span>
            </div>
            <div class="ps-1 fs-xxs text-muted">{{min}} min</div>
          </template>
          <div class="fs-xxs text-muted mt-auto">Proc/p99/Total</div>
        </div>
        
        <div class="d-none col-lg-2 text-center d-lg-flex flex-column align-items-center">
          <div v-for="(d,i) in (server.monitorData.topUsers||[]).slice(0,3)" class="text-nowrap fs-xs" :class="{'mt-auto':i===0}"><a :href="'/internal/reports?uid='+d.userId">{{maskEmail(d.userId)}}</a> ({{d.count}})</div>
          <div class="fs-xxs text-muted mt-auto">Top users (5m) <a v-if="server.monitorData.topUsers && server.monitorData.topUsers.length>3" href="#" class="ms-1" @click.prevent="openUserDetailsModal(server)">more&hellip;</a></div>
        </div>
        <div class="d-none col-lg-2 text-center d-lg-flex flex-column align-items-center">
          <div v-for="(d,i) in (server.monitorData.errorUsers||[]).slice(0,3)" class="text-nowrap fs-xs" :class="{'mt-auto':i===0}" :title="errorUserTooltip(d)"><a :href="'/internal/reports?uid='+d.userId">{{maskEmail(d.userId)}}</a> <span class="text-danger">{{d.unexpected||''}}</span><span v-if="d.unexpected && (d.handled||d.license)">/</span><span class="text-warning">{{d.handled||''}}</span><span v-if="d.handled && d.license">/</span><span class="text-info">{{d.license||''}}</span></div>
          <div class="fs-xxs text-muted mt-auto">Error users (5m) <span class="text-danger">U</span>/<span class="text-warning">H</span>/<span class="text-info">L</span> <a v-if="server.monitorData.errorUsers && server.monitorData.errorUsers.length>3" href="#" class="ms-1" @click.prevent="openUserDetailsModal(server)">more&hellip;</a></div>
        </div>
        
        <div v-bind:class="[server.id]" class="col-12 text-center chart-container" :class="expandedChart ? 'chart-expanded' : 'col-lg-4'">
            <svg class="activitychart"></svg>
            <div class="chart-tooltip" style="display:none;"></div>
        </div>
      </div>
    </div>

    <!-- Server Status Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverStatus.isOpen }" 
         :style="{ display: modals.serverStatus.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverStatusModalLabel" 
         :aria-hidden="!modals.serverStatus.isOpen"
         @click.self="closeModal('serverStatus')">
      
      <div v-if="modals.serverStatus.isOpen" class="modal-backdrop fade show" @click="closeModal('serverStatus')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down modal-xl">
        <div class="modal-content" style="height:90vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverStatusModalLabel">Server status</h5>
            <button type="button" class="btn-close" @click="closeModal('serverStatus')" aria-label="Close"></button>
          </div>
          
          <div class="modal-body overflow-auto">
            <table class="table table-striped table-sm fs-sm">
              <thead>
              <tr><th>Name</th><th>Value</th></tr>    
              </thead>
              <tbody>
                <tr v-for="property in detailedServerStatus">
                  <td>{{property.name}}</td>
                  <td>{{property.value}}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Server Log Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverLog.isOpen }" 
         :style="{ display: modals.serverLog.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverLogModalLabel" 
         :aria-hidden="!modals.serverLog.isOpen"
         @click.self="closeModal('serverLog')">
      
      <div v-if="modals.serverLog.isOpen" class="modal-backdrop fade show" @click="closeModal('serverLog')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-lg-down modal-xl">
        <div class="modal-content" style="height:95vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverLogModalLabel">Server Logs</h5>
            <button type="button" class="btn-close" @click="closeModal('serverLog')" aria-label="Close"></button>
          </div>
          
          <div class="modal-body mh-100">
            <div class="row align-items-center ">
              <div class="col-12 col-md-6 d-flex">
                <div class="form-check ps-5 text-nowrap" v-for="(level,i) in ALLLOGLEVELS">
                  <input class="form-check-input" type="checkbox" :value="level" :id="'levelCheck_'+i" v-model="logLevels">
                    <label class="form-check-label" :for="'levelCheck_'+i">
                      {{level}} - {{countLogByLevel(level)}}
                    </label>
                </div>
              </div>
              <div class="col-8 col-md-4 ">
                <input v-model="logFilter" class="form-control form-control-sm" type="text" placeholder="Filter">  
              </div>
              <div class="col-4 col-md-2 fs-xs">
                Showing {{filteredServerLog.length}}/{{serverLog.length}}
                <button class="btn btn-sm btn-secondary p-2" type="button" @click="refreshLog(serverLogServer)" title="Refresh"><i class="bi bi-arrow-counterclockwise"></i></button>
              </div>
            </div>
            
            <div class="table-responsive" style="max-height:90%">
              <table class="table table-striped table-sm fs-sm">
                <thead>
                <tr>
                  <th>DateTime</th>
                  <th>Level</th>
                  <th>Message</th>
                </tr>    
                </thead>
                <tbody>
                  <tr v-for="line in filteredServerLog">
                    <td class="text-nowrap">{{line[0]}}</td>
                    <td>{{line[1]}}</td>
                    <td>{{line[2]}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Server Job Viewer Modal with Vue state control -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverJobViewer.isOpen }" 
         :style="{ display: modals.serverJobViewer.isOpen ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="serverJobViewerModalLabel" 
         :aria-hidden="!modals.serverJobViewer.isOpen"
         @click.self="closeModal('serverJobViewer')">
      
      <div v-if="modals.serverJobViewer.isOpen" class="modal-backdrop fade show" @click="closeModal('serverJobViewer')"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down modal-xl">
        <div class="modal-content" style="height:90vh;">
          <div class="modal-header">
            <h5 class="modal-title" id="serverJobViewerModalLabel">Server Jobs</h5>
            <button type="button" class="btn-close" @click="closeModal('serverJobViewer')" aria-label="Close"></button>
          </div>
          <div class="modal-body overflow-auto">
            <div class="d-flex align-items-center justify-content-between fs-xs">
              <label class="form-check form-check-inline mb-0"><input type="checkbox" class="form-check-input" v-model="hideSystemJobs"> <span class="form-check-label">Hide System jobs</span></label>
              <span>Showing {{filteredServerJobs.length}}/{{selectedServerJobs.length}}</span>
            </div>
            <table class="table table-striped table-sm fs-sm">
              <thead>
              <tr><th>Info</th><th>Queue</th><th>Execution</th><th>Pool</th><th>Status</th><th>Id</th><th>Name</th></tr>    
              </thead>
              <tbody>
                <tr v-for="d in filteredServerJobs">
                  <td><a :href="'/internal/reports?uid='+d.info">{{d.info}}</a></td>
                  <td>{{d.queueTime}}</td>
                  <td>{{d.execTime}}</td>
                  <td>{{d.pool}}</td>
                  <td>{{getStatusText(d.status)}}</td>
                  <td>{{d.id}}</td>
                  <td>{{d.name}}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Server Editor Modal -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.serverEditor.isOpen }"
         :style="{ display: modals.serverEditor.isOpen ? 'block' : 'none' }"
         tabindex="-1" @click.self="closeModal('serverEditor')">
      <div v-if="modals.serverEditor.isOpen" class="modal-backdrop fade show" @click="closeModal('serverEditor')"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Edit Servers</h5>
            <button type="button" class="btn-close" @click="closeModal('serverEditor')" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <table class="table table-sm fs-sm server-editor-table">
              <thead>
                <tr><th>ID</th><th>Name</th><th>Group</th><th>Server Root URL</th><th></th></tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in editServers" :key="i">
                  <td><input v-model="s.id" class="form-control form-control-sm" placeholder="WeatherN"></td>
                  <td><input v-model="s.name" class="form-control form-control-sm" placeholder="Weather N"></td>
                  <td><input v-model="s.group" class="form-control form-control-sm" placeholder="API"></td>
                  <td><input v-model="s.serverroot" class="form-control form-control-sm" placeholder="https://..."></td>
                  <td><button class="btn btn-sm btn-outline-danger" @click="removeEditServer(i)" title="Remove">&times;</button></td>
                </tr>
              </tbody>
            </table>
            <button class="btn btn-sm btn-outline-primary" @click="addEditServer()">+ Add Server</button>
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-outline-warning" @click="resetServers()">Reset to Defaults</button>
            <button class="btn btn-sm btn-secondary" @click="closeModal('serverEditor')">Cancel</button>
            <button class="btn btn-sm btn-primary" @click="saveServers()">Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- User Details Modal -->
    <div class="modal" :class="{ 'fade': true, 'show': modals.userDetails.isOpen }"
         :style="{ display: modals.userDetails.isOpen ? 'block' : 'none' }"
         tabindex="-1" @click.self="closeModal('userDetails')">
      <div v-if="modals.userDetails.isOpen" class="modal-backdrop fade show" @click="closeModal('userDetails')"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{userDetailsServer ? userDetailsServer.name : ''}} — Users (5 min window)</h5>
            <button type="button" class="btn-close" @click="closeModal('userDetails')" aria-label="Close"></button>
          </div>
          <div class="modal-body overflow-auto" style="max-height:70vh;" v-if="userDetailsServer && userDetailsServer.monitorData">
            <div class="row">
              <div class="col-6">
                <h6 class="fs-sm fw-bold">Top Users</h6>
                <table class="table table-sm table-striped fs-sm">
                  <thead><tr><th>#</th><th>User</th><th>Requests</th></tr></thead>
                  <tbody>
                    <tr v-for="(d,i) in (userDetailsServer.monitorData.topUsers||[])">
                      <td>{{i+1}}</td>
                      <td><a :href="'/internal/reports?uid='+d.userId">{{maskEmail(d.userId)}}</a></td>
                      <td>{{d.count}}</td>
                    </tr>
                    <tr v-if="!userDetailsServer.monitorData.topUsers || userDetailsServer.monitorData.topUsers.length===0"><td colspan="3" class="text-muted text-center">No data</td></tr>
                  </tbody>
                </table>
              </div>
              <div class="col-6">
                <h6 class="fs-sm fw-bold">Error Users <span class="text-danger">U</span>/<span class="text-warning">H</span>/<span class="text-info">L</span></h6>
                <table class="table table-sm table-striped fs-sm">
                  <thead><tr><th>#</th><th>User</th><th class="text-danger">Unhandled</th><th class="text-warning">Handled</th><th class="text-info">License</th><th>Total</th></tr></thead>
                  <tbody>
                    <tr v-for="(d,i) in (userDetailsServer.monitorData.errorUsers||[])">
                      <td>{{i+1}}</td>
                      <td><a :href="'/internal/reports?uid='+d.userId">{{maskEmail(d.userId)}}</a></td>
                      <td class="text-danger">{{d.unexpected||''}}</td>
                      <td class="text-warning">{{d.handled||''}}</td>
                      <td class="text-info">{{d.license||''}}</td>
                      <td>{{d.total}}</td>
                    </tr>
                    <tr v-if="!userDetailsServer.monitorData.errorUsers || userDetailsServer.monitorData.errorUsers.length===0"><td colspan="6" class="text-muted text-center">No errors</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CommonModals />
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_serveradmin";
import {VcDateUtils} from "/src/visualcrossing-core/vcdateutils";
import CommonModals from '/src/components/CommonModals.vue';
import '/src/website-core/VcUtils';
import * as d3 from "d3";

var DEFAULT_SERVERS=[
  {id:"Weather1", name:"Weather 1", group:"Bulk", serverroot:"https://weather1.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
  {id:"Weather2", name:"Weather 2", group:"API", serverroot:"https://weather2.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
  {id:"Weather3", name:"Weather 3", group:"API", serverroot:"https://weather3.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
  {id:"Weather4", name:"Weather 4", group:"API", serverroot:"https://weather4.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
  {id:"Weather5", name:"Weather 5", group:"API", serverroot:"https://weather5.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
  {id:"Weather6", name:"Weather 6", group:"Dev", serverroot:"https://weather6.visualcrossing.com/VisualCrossingWebServices/rest/services/"},
];
var SERVERS_STORAGE_KEY="vc_monitor_servers";

function loadServersFromStorage() {
  try {
    var json=localStorage.getItem(SERVERS_STORAGE_KEY);
    if (json) {
      var saved=JSON.parse(json);
      if (Array.isArray(saved) && saved.length>0) {
        return saved.map(function(s) { return {id:s.id, name:s.name, group:s.group||"Default", serverroot:s.serverroot, monitorData:{}, failCount:0, backoffUntil:0, paused:false}; });
      }
    }
  } catch(e) { console.warn("Failed to load servers from localStorage",e); }
  return DEFAULT_SERVERS.map(function(s) { return {id:s.id, name:s.name, group:s.group, serverroot:s.serverroot, monitorData:{}, failCount:0, backoffUntil:0, paused:false}; });
}

export default {
  name: 'serverminitoring',
  props: {
    
  },
  data() {
    return {
      // Modal state management
      modals: {
        serverStatus: { isOpen: false },
        serverLog: { isOpen: false },
        serverJobViewer: { isOpen: false },
        serverEditor: { isOpen: false },
        userDetails: { isOpen: false }
      },

      // Your existing data properties
      ALLLOGLEVELS:["ERROR","WARN","INFO","DEBUG"],
      servers: loadServersFromStorage(),
      editServers: [],
      refreshinterval:10,
      enableautorefresh:true,
      sessionState:null,
      lastrefresh:0,
      epochStart:1609459200,
      statisticsMins:[5,60,720],
      selectedServer:null,
      userDetailsServer:null,
      historyFilterSecsAgo:10800,
      resolution:500,
      chartBinSeconds:60,
      aggregationOptions:[
        {label:"1 min", value:60},
        {label:"5 min", value:300},
        {label:"15 min", value:900},
        {label:"60 min", value:3600},
      ],
      timeWindowOptions:[
        {label:"15 min", value:900},
        {label:"1 hr", value:3600},
        {label:"3 hr", value:10800},
        {label:"12 hr", value:43200},
        {label:"Max", value:0},
      ],
      serverLog:[],
      serverLogServer:null,
      maxJobDisplayCount:250,
      hideSystemJobs:true,
      logLevels:[],
      logFilter:null,
      waitingForSessionAttempt:true,
    }
  },
  methods:{
    // New modal control methods
    openModal(modalName) {
      this.modals[modalName].isOpen = true;
    },

    closeModal(modalName) {
      this.modals[modalName].isOpen = false;
    },

    openUserDetailsModal(server) {
      this.userDetailsServer = server;
      this.openModal('userDetails');
    },

    closeAllModals() {
      Object.keys(this.modals).forEach(key => {
        this.modals[key].isOpen = false;
      });
    },

    // Server editor methods
    openServerEditor() {
      this.editServers = this.servers.map(function(s) {
        return { id: s.id, name: s.name, group: s.group || "Default", serverroot: s.serverroot };
      });
      this.openModal('serverEditor');
    },
    addEditServer() {
      var n = this.editServers.length + 1;
      this.editServers.push({ id: "Server" + n, name: "Server " + n, group: "Default", serverroot: "" });
    },
    removeEditServer(index) {
      this.editServers.splice(index, 1);
    },
    saveServers() {
      var toSave = this.editServers.map(function(s) {
        return { id: s.id.trim(), name: s.name.trim(), group: (s.group || "Default").trim(), serverroot: s.serverroot.trim() };
      }).filter(function(s) { return s.id && s.serverroot; });
      if (toSave.length === 0) return;
      try { localStorage.setItem(SERVERS_STORAGE_KEY, JSON.stringify(toSave)); } catch(e) { console.warn("Failed to save servers", e); }
      this.servers = toSave.map(function(s) { return { id: s.id, name: s.name, group: s.group, serverroot: s.serverroot, monitorData: {}, failCount:0, backoffUntil:0, paused:false }; });
      this.closeModal('serverEditor');
      this.lastrefresh = 0;
      this.refreshMonitor();
    },
    resetServers() {
      try { localStorage.removeItem(SERVERS_STORAGE_KEY); } catch(e) {}
      this.editServers = DEFAULT_SERVERS.map(function(s) {
        return { id: s.id, name: s.name, group: s.group, serverroot: s.serverroot };
      });
    },

    // Specific modal opening methods
    setTimeWindow(secsAgo) {
      var me=this;
      me.historyFilterSecsAgo=secsAgo;
      // Wait for Vue to re-render layout (expanded vs compact) before rebuilding
      me.$nextTick(function() {
        me.servers.forEach(function(server) {
          if (server.monitorData && server.monitorData.monitors) {
            me.processMonitorData(server, null);
          }
        });
        me.refreshAllCharts();
      });
    },

    setAggregation(binSeconds) {
      var me=this;
      me.chartBinSeconds=binSeconds;
      me.servers.forEach(function(server) {
        if (server.monitorData && server.monitorData.monitors) {
          me.processMonitorData(server, null);
        }
      });
      me.refreshAllCharts();
    },

    openServerStatusModal(server) {
      this.selectedServer = server;
      this.openModal('serverStatus');
    },

    openServerLogModal(server) {
      this.refreshLog(server);
      this.openModal('serverLog');
    },

    openServerJobViewerModal(server) {
      this.selectedServer = server;
      this.openModal('serverJobViewer');
    },

    // Handle ESC key
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        // Find and close the first open modal
        const openModal = Object.keys(this.modals).find(key => this.modals[key].isOpen);
        if (openModal) {
          this.closeModal(openModal);
        }
      }
    },

    // Your existing methods stay exactly the same
    setLogFilter(level) {
      var me=this;
      let index= me.logLevels.indexOf(level);
      if (index>=0) {
        me.logLevels.splice(index,1);
      } else {
        me.logLevels.push(level);
      }
    },

    getStatusText(status) {
      switch (status) {
        case 0: return "Unknown";
        case 1: return "Queued";
        case 2: return "Executing";
        case 3: return "Complete";
        case 4: return "Error";
        case 5: return "Cancelled";
        default: return "? " +status;
      }
    },

    errorCount(server) {
      let levelCounts=server && server.statusDetailed && server.statusDetailed.levelCounts;
      if (!levelCounts) return "-";
      return levelCounts.fatal+levelCounts.error;
    },

    warnCount(server) {
      let levelCounts=server && server.statusDetailed && server.statusDetailed.levelCounts;
      if (!levelCounts) return "-";
      return levelCounts.warn;
    },

    refreshLog(server) {
      var me=this;
      me.serverLog=[];
      me.serverLogServer=server;
      if (!me.isAdmin) return;
      vcCommunicator.serverlog(server.serverroot)
          .then(function (log) {
            if (!log.content) return;
            let lines=log.content.split("\n");

            lines=lines.map(function(d,i) {
              if (i===0) return null;
              let row= d.split("\t");
              while (row.length<3) {
                row.splice(row,0,"")
              }
              return row;
            });
            lines=lines.filter(function(d) {
              return d!==null;
            })
            me.serverLog=lines;
          })
          .catch((error) => {
             me.serverLog=error;
          });
    },

    getActiveJobCount(server) {
        if (!server || !server.monitorData || !server.monitorData.jobs) return "-";
        if (this.hideSystemJobs) return server.monitorData.jobs.filter(function(j){return j.pool!=="System";}).length;
        return server.monitorData.jobs.length;
    },

    // Remove the old toggleModal method since we're using Vue state now
    
    getTime:function() {
        return new Date().getTime();
    },

    getTimeToEpoch:function() {
       return (this.getTime()/1000)-this.epochStart;
    },

    format:function(v,d,suffix) {
        if (!v && v!==0) return " - ";
        if (d) v=v.toFixed(d);
        if (suffix) return v+suffix;
        return v;
    },

    formatGb(v) {
        return this.format(v/1024, 1)
    },

    formatCompact(v) {
        if (!v) return "";
        if (v >= 1000) return (v / 1000).toFixed(1) + "k";
        return String(v);
    },

    maskEmail(email) {
      if (!email) return email;
      var at=email.indexOf('@');
      if (at<=0) return email;
      var local=email.substring(0,at);
      var domain=email.substring(at);
      if (local.length<=1) return local+'***'+domain;
      return local.charAt(0)+'***'+local.charAt(local.length-1)+domain;
    },

    errorUserTooltip(d) {
      if (!d) return '';
      var parts=[];
      if (d.unexpected) parts.push('Unhandled: '+d.unexpected);
      if (d.handled) parts.push('Handled: '+d.handled);
      if (d.license) parts.push('License: '+d.license);
      parts.push('Total: '+d.total);
      return this.maskEmail(d.userId)+' — '+parts.join(', ');
    },

    // ... rest of your existing methods stay exactly the same
    refreshMonitor:function() {
      var me=this;
      if (!me.isAdmin) return;
      var sinceLastRefresh=me.lastrefresh && Math.ceil((me.getTime()-me.lastrefresh)/1000);
      var servers=me.servers;
      var secondsBack=sinceLastRefresh>0 && sinceLastRefresh<120?sinceLastRefresh:0;
      var now=me.getTime();

      servers.forEach(function(server, index) {
        // Skip paused servers entirely — user must manually resume
        if (server.paused) return;
        // Skip servers still in backoff window
        if (server.backoffUntil && now < server.backoffUntil) return;

        vcCommunicator.monitor(secondsBack, server.serverroot)
          .then(function (results) {
            // Success — reset backoff
            server.failCount=0;
            server.backoffUntil=0;
            var existingMonitorEvents=server && server.monitorData && server.monitorData.monitors;
            results.freememorypercent=0;
            if (!results.errorUsers) results.errorUsers=[];
            if (!results.topUsers) results.topUsers=[];
            server["monitorData"]=results;
            me.lastrefresh=me.getTime();
            me.processMonitorData(server, existingMonitorEvents);
            // Redraw ALL charts so global y-scales stay in sync
            me.refreshAllCharts();
          })
          .catch((error) => {
            console.log(server.name + " monitor error:", error);
            me.applyBackoff(server);
          });

        vcCommunicator.serverstatus(server.serverroot)
          .then(function (results) {
            server.status=results.status;
            server.statusDetailed=results;
            me.lastrefresh=me.getTime();
          })
          .catch((error) => {
            console.log(server.name + " status error:", error);
            server.status="COMM_ERR";
            server.statusDetailed={};
            me.lastrefresh=me.getTime();
            me.applyBackoff(server);
          });
      });
      
      if (me.enableautorefresh) {
        setTimeout(function() {
          me.refreshMonitor()
        }, me.refreshinterval*1000);
      }
    },

    // Backoff: 10s, 20s, 40s, 80s, 160s, then pause (requires manual resume)
    applyBackoff:function(server) {
      server.failCount = (server.failCount || 0) + 1;
      var MAX_RETRIES = 6;
      if (server.failCount >= MAX_RETRIES) {
        server.paused = true;
        server.backoffUntil = 0;
      } else {
        // Exponential backoff: 10s * 2^(failCount-1)
        var delaySec = 10 * Math.pow(2, server.failCount - 1);
        server.backoffUntil = this.getTime() + delaySec * 1000;
      }
    },

    resumeServer:function(server) {
      server.failCount = 0;
      server.backoffUntil = 0;
      server.paused = false;
      this.refreshMonitor();
    },

    getServerBackoffLabel:function(server) {
      if (server.paused) return "Paused";
      if (server.backoffUntil) {
        var secsLeft = Math.max(0, Math.ceil((server.backoffUntil - this.getTime()) / 1000));
        return secsLeft + "s";
      }
      return null;
    },

    processMonitorData:function(server, existingMonitorEvents) {
      var me=this;
      
      if (!server || ! server.monitorData) return;
      var  monitorData= server.monitorData;
      monitorData.freememorypercent=Math.round(100*monitorData.freeMemory/monitorData.totalMemory);

      if (!monitorData || !monitorData.monitors || monitorData.monitors.length===0) return;
      
      var monitors=monitorData.monitors;
      if (existingMonitorEvents && existingMonitorEvents.length>0) {
        var oldIndex=existingMonitorEvents.length-1;
        var oldestNewTime=monitors[0].time;
        while (oldIndex>=0 && existingMonitorEvents[oldIndex].time>oldestNewTime) {
          oldIndex--;
        }
        if (oldIndex>=0) {
          existingMonitorEvents=existingMonitorEvents.slice(0,oldIndex);
          monitors.splice(0, 0, ...existingMonitorEvents);
        }
      }

      var now=me.getTimeToEpoch();
      
      var stats={};
      me.statisticsMins.forEach(function(d) {
        stats[d]={
          success:0,
          error:0,
          unhandled:0,
          handled:0,
          license:0,
          execTime:0,
          totalExecTime:0,
          errorRate:0,
          meanTime:0,
          meanTotalTime:0,
          p99ExecTime:0,
          p99TotalExecTime:0,
        }
      });
      //Vue.set(monitorData, "stats", stats) 
      monitorData["stats"]=stats;
      var epochNow=me.getTimeToEpoch();
      var basetime=me.historyFilterSecsAgo?(epochNow-me.historyFilterSecsAgo) : monitors[0].time; //me.getStartOfDayToEpoch(); //
      //var timespan=me.historyFilterSecsAgo || (epochNow-monitors[0].time);
      var timeperbin=me.chartBinSeconds || (me.historyFilterSecsAgo?me.historyFilterSecsAgo/me.resolution:120);
      // Quantize basetime to bin boundaries so bins are stable across refreshes
      basetime=Math.floor(basetime/timeperbin)*timeperbin;
      //console.log("Timeperbin:"+timeperbin);
      var currentBinIndex=-1, currentBin;

      monitorData.bins= monitors.reduce(function(a, d, i) {
        var binIndex=Math.floor((d.time-basetime)/timeperbin);
        if (binIndex<0) return a;
        if (!currentBin || currentBinIndex!==binIndex) {
          currentBin={
            time:binIndex*timeperbin+basetime,
            totalerrors:d.unexpected+d.license+d.handled+d.unknown,
            unhandlederrors:d.unexpected+d.unknown,
            licenseerrors:d.license,
            handlederrors:d.handled,
            success:d.success,
            successExecTime:d.successExecTime,
            successTotalExecTime:d.successTotalExecTime||0,
            _p80ExecSum:d.p80ExecTime*(d.success||1),
            _p99ExecMax:d.p99ExecTime||0,
            _p80TotalSum:d.p80TotalExecTime*(d.success||1),
            _p99TotalMax:d.p99TotalExecTime||0,
            _successForP:d.success||0
          };
          a.push(currentBin);
          currentBinIndex=binIndex;
        } else {
          currentBin.totalerrors+=d.unexpected+d.license+d.handled+d.unknown;
          currentBin.unhandlederrors+=d.unexpected+d.unknown,
          currentBin.licenseerrors+=d.license;
          currentBin.handlederrors+=d.handled;
          currentBin.success+=d.success;
          currentBin.successExecTime+=d.successExecTime;
          currentBin.successTotalExecTime+=(d.successTotalExecTime||0);
          currentBin._p80ExecSum+=d.p80ExecTime*(d.success||1);
          currentBin._p99ExecMax=Math.max(currentBin._p99ExecMax, d.p99ExecTime||0);
          currentBin._p80TotalSum+=d.p80TotalExecTime*(d.success||1);
          currentBin._p99TotalMax=Math.max(currentBin._p99TotalMax, d.p99TotalExecTime||0);
          currentBin._successForP+=(d.success||0);
        }
        return a;
      }, []);
      
      monitorData.bins.forEach(function(d, i) {
        d.successpersecond=d.success/timeperbin;
        d.meanExecTime=d.successExecTime/d.success;
        d.meanTotalExecTime=d.successTotalExecTime/d.success;
        d.p80ExecTime=d._successForP>0 ? Math.round(d._p80ExecSum/d._successForP) : 0;
        d.p99ExecTime=d._p99ExecMax;
        d.p80TotalExecTime=d._successForP>0 ? Math.round(d._p80TotalSum/d._successForP) : 0;
        d.p99TotalExecTime=d._p99TotalMax;

        me.statisticsMins.forEach(function(min, i) {
          if (!monitorData.stats) return;
          var s=monitorData.stats[min];
          
          if (s && (!min ||  d.time>(now-min*60))) {
            s.success+=d.success;
            s.error+=d.totalerrors;
            s.unhandled+=d.unhandlederrors;
            s.handled+=d.handlederrors;
            s.license+=d.licenseerrors;
            s.execTime+=d.successExecTime;
            s.totalExecTime+=d.successTotalExecTime;
            if (d.p99ExecTime>s.p99ExecTime) s.p99ExecTime=d.p99ExecTime;
            if (d.p99TotalExecTime>s.p99TotalExecTime) s.p99TotalExecTime=d.p99TotalExecTime;
          }

        });
      });

      me.statisticsMins.forEach(function(d, i) {
        if (!monitorData.stats) return;
        var s=monitorData.stats[d];
        if (s) {
          s.errorRate=100*s.error/(s.success+s.error);
          s.meanTime=Math.round(s.execTime/s.success);
          s.meanTotalTime=Math.round(s.totalExecTime/s.success);
        }
      });

    },

    countLogByLevel(level) {
      var me=this;
      var serverLog=me.serverLog;
      if (!serverLog) return 0;
      return serverLog.reduce(function(a,d) {
        return d[1]===level?a+1:a;
      },0);
    },

        refreshHistoryChart:function(server) {
        var me=this;
        if (!server.monitorData || !server.monitorData.bins || server.monitorData.bins.length===0) return;

        var svg = d3.select("."+server.id+" .activitychart");
        if (!svg || !svg.node()) return;

        // --- Dimensions ---
        // Use the container width; SVG fills it via CSS width:100%
        var containerEl = svg.node().parentNode;
        var containerWidth = containerEl ? containerEl.clientWidth : 600;
        if (containerWidth < 50) containerWidth = 600; // fallback if not laid out yet

        var svgHeight = me.expandedChart ? 180 : 100;
        var margin = { top: 10, right: 40, bottom: 22, left: 38 };
        var width = containerWidth - margin.left - margin.right;
        var height = svgHeight - margin.top - margin.bottom;

        // Set explicit SVG dimensions (avoids getBoundingClientRect issues)
        svg.attr("width", containerWidth).attr("height", svgHeight);

        // --- Data (use group-synced scales from refreshAllCharts) ---
        var grp = server.group || "default";
        var groupScales = (me._groupScales && me._groupScales[grp]) || { maxReqs:1, maxErrors:1, maxExecTime:1, xMin:0, xMax:me.getTimeToEpoch() };
        var xMin = groupScales.xMin;
        var xMax = groupScales.xMax;

        var allBins = server.monitorData.bins;
        var data = allBins.filter(function(d) { return d.time >= xMin; });
        if (data.length === 0) return;

        var x = d3.scaleLinear()
          .range([0, width])
          .domain([xMin, xMax]);

        var y = d3.scaleLinear()
          .range([height, 0])
          .domain([0, groupScales.maxReqs]);

        var yerr = d3.scaleLinear()
          .range([height, 0])
          .domain([0, Math.max(1, 1.5 * groupScales.maxErrors)]);

        var yexectime = d3.scaleLinear()
          .range([height, 0])
          .domain([0, groupScales.maxExecTime]);

        // Error bar width in pixels based on bin size
        var binW = width * (me.chartBinSeconds || 60) / (x.domain()[1] - x.domain()[0]);
        if (binW < 2) binW = 2;
        if (binW > width / 4) binW = width / 4;

        // --- Clear and rebuild (avoids all stale-state bugs) ---
        svg.selectAll("*").remove();

        // Root group with margins
        var g = svg.append("g")
          .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

        // Clip path
        svg.append("defs").append("clipPath")
          .attr("id", "clip_" + server.id)
          .append("rect")
            .attr("width", width)
            .attr("height", height);

        var chartG = g.append("g")
          .attr("clip-path", "url(#clip_" + server.id + ")");

        // --- Grid lines ---
        g.append("g")
          .attr("class", "grid-lines")
          .call(d3.axisLeft(y).ticks(5).tickSize(-width).tickFormat(""))
          .call(function(sel) { sel.select(".domain").remove(); });

        // --- Stacked error bars ---
        var errorData = data.filter(function(d) { return d.totalerrors > 0; });
        if (errorData.length > 0) {
          var errorColors = {
            "unhandlederrors": "#d73027",
            "handlederrors": "#e8870e",
            "licenseerrors": "#e8d30e"
          };
          var stackedData = d3.stack()
            .keys(["licenseerrors", "handlederrors", "unhandlederrors"])(errorData);

          stackedData.forEach(function(layer) {
            chartG.append("g")
              .attr("fill", errorColors[layer.key])
              .attr("opacity", 0.7)
              .selectAll("rect")
              .data(layer)
              .enter().append("rect")
                .attr("x", function(d) { return x(d.data.time) - binW / 2; })
                .attr("y", function(d) { return yerr(d[1]); })
                .attr("width", binW)
                .attr("height", function(d) { return Math.max(0, yerr(d[0]) - yerr(d[1])); });
          });
        }

        // --- Success area ---
        chartG.append("path")
          .attr("class", "success-area")
          .datum(data)
          .attr("d", d3.area()
            .defined(function(d) { return d.successpersecond; })
            .x(function(d) { return x(d.time); })
            .y0(height)
            .y1(function(d) { return y(d.successpersecond); })
            .curve(d3.curveMonotoneX));

        // --- Success line ---
        chartG.append("path")
          .attr("class", "success")
          .datum(data)
          .attr("d", d3.line()
            .defined(function(d) { return d.successpersecond; })
            .x(function(d) { return x(d.time); })
            .y(function(d) { return y(d.successpersecond); })
            .curve(d3.curveMonotoneX));

        // --- Exec time line (mean processing) ---
        chartG.append("path")
          .attr("class", "exectime")
          .datum(data)
          .attr("d", d3.line()
            .defined(function(d) { return d.meanExecTime; })
            .x(function(d) { return x(d.time); })
            .y(function(d) { return yexectime(d.meanExecTime); })
            .curve(d3.curveMonotoneX));

        // --- p99 Exec time line ---
        chartG.append("path")
          .attr("class", "p99exectime")
          .datum(data)
          .attr("d", d3.line()
            .defined(function(d) { return d.p99ExecTime; })
            .x(function(d) { return x(d.time); })
            .y(function(d) { return yexectime(d.p99ExecTime); })
            .curve(d3.curveMonotoneX));

        // --- Total exec time line (mean round-trip) ---
        chartG.append("path")
          .attr("class", "totalexectime")
          .datum(data)
          .attr("d", d3.line()
            .defined(function(d) { return d.meanTotalExecTime; })
            .x(function(d) { return x(d.time); })
            .y(function(d) { return yexectime(d.meanTotalExecTime); })
            .curve(d3.curveMonotoneX));

        // --- X-axis ticks ---
        var timeSpan = x.domain()[1] - x.domain()[0];
        var tickInterval;
        if (timeSpan <= 600) tickInterval = 60;
        else if (timeSpan <= 1800) tickInterval = 300;
        else if (timeSpan <= 3600) tickInterval = 600;
        else if (timeSpan <= 10800) tickInterval = 1800;
        else if (timeSpan <= 21600) tickInterval = 3600;
        else if (timeSpan <= 86400) tickInterval = 7200;
        else tickInterval = 14400;

        var ticks = [];
        var t = Math.ceil((x.domain()[0] + me.epochStart) / tickInterval) * tickInterval - me.epochStart;
        while (t <= x.domain()[1]) {
          ticks.push(t);
          t += tickInterval;
        }

        var tickFormatFn = timeSpan > 21600
          ? function(d) {
              var dt = new Date((d + me.epochStart) * 1000);
              return (dt.getMonth()+1) + "/" + dt.getDate() + " " + VcDateUtils.formatTime(d + me.epochStart);
            }
          : function(d) { return VcDateUtils.formatTime(d + me.epochStart); };

        g.append("g")
          .attr("class", "time-axis")
          .attr("transform", "translate(0," + height + ")")
          .call(d3.axisBottom(x).tickValues(ticks).tickSize(-5).tickFormat(tickFormatFn));

        // --- Left Y-axis (Req/s, green) ---
        g.append("g")
          .attr("class", "y-axis")
          .call(d3.axisLeft(y).ticks(5))
          .call(function(sel) {
            sel.selectAll("text").style("fill", "#1a961a");
            sel.selectAll("line").style("stroke", "#1a961a");
            sel.select(".domain").style("stroke", "#1a961a");
          });

        // --- Right Y-axis (Exec time, gray) ---
        g.append("g")
          .attr("class", "yexec-axis")
          .attr("transform", "translate(" + width + ",0)")
          .call(d3.axisRight(yexectime).ticks(4).tickFormat(function(d) { return d ? Math.round(d) + "ms" : "0"; }))
          .call(function(sel) {
            sel.selectAll("text").style("fill", "#555").style("font-size", "9px");
            sel.selectAll("line").style("stroke", "#555");
            sel.select(".domain").style("stroke", "#555");
          });

        // --- Hover overlay + tooltip ---
        var tooltipEl = d3.select("." + server.id + " .chart-tooltip");
        var hoverLine = g.append("line")
          .attr("class", "hover-line")
          .attr("y1", 0).attr("y2", height)
          .style("display", "none");

        g.append("rect")
          .attr("width", width).attr("height", height)
          .style("fill", "none").style("pointer-events", "all")
          .on("mousemove", function(event) {
            var mouseX = d3.pointer(event, this)[0];
            var xVal = x.invert(mouseX);
            var closest = null, closestDist = Infinity;
            data.forEach(function(d) {
              var dist = Math.abs(d.time - xVal);
              if (dist < closestDist) { closestDist = dist; closest = d; }
            });
            if (!closest) return;
            var px = x(closest.time);
            hoverLine.attr("x1", px).attr("x2", px).style("display", null);
            var timeStr = VcDateUtils.formatTime(closest.time + me.epochStart);
            var execMean = closest.meanExecTime ? Math.round(closest.meanExecTime) + "ms" : "-";
            var execP99 = closest.p99ExecTime ? closest.p99ExecTime + "ms" : "-";
            var totalMean = closest.meanTotalExecTime ? Math.round(closest.meanTotalExecTime) + "ms" : "-";
            var totalP99 = closest.p99TotalExecTime ? closest.p99TotalExecTime + "ms" : "-";
            tooltipEl.style("display", "block")
              .html(
                "<strong>" + timeStr + "</strong><br>" +
                "<span style='color:#1a961a'>\u25CF</span> Req/s: " + (closest.successpersecond ? closest.successpersecond.toFixed(1) : "0") + "<br>" +
                "<span style='color:#555'>\u25CF</span> Proc: " + execMean + " <span style='color:#999;font-size:10px'>p99 " + execP99 + "</span><br>" +
                "<span style='color:#3366cc'>\u25CF</span> Total: " + totalMean + " <span style='color:#999;font-size:10px'>p99 " + totalP99 + "</span><br>" +
                "<span style='color:#d73027'>\u25CF</span> Unhandled: " + (closest.unhandlederrors || 0) +
                " <span style='color:#e8870e'>\u25CF</span> Handled: " + (closest.handlederrors || 0) +
                " <span style='color:#e8d30e'>\u25CF</span> License: " + (closest.licenseerrors || 0)
              );
            var tipLeft = px + margin.left + 12;
            if (tipLeft + 130 > containerWidth) tipLeft = px + margin.left - 140;
            tooltipEl.style("left", tipLeft + "px").style("top", "0px");
          })
          .on("mouseleave", function() {
            hoverLine.style("display", "none");
            tooltipEl.style("display", "none");
          });
      },

      // Compute per-group scale maxes, then render all charts
      refreshAllCharts:function() {
        var me=this;
        var now = me.getTimeToEpoch();

        // Collect unique groups
        var groups = {};
        me.servers.forEach(function(s) {
          var grp = s.group || "default";
          if (!groups[grp]) groups[grp] = { servers: [], maxReqs: 1, maxErrors: 1, execTimeValues: [], xMin: now };
          groups[grp].servers.push(s);
        });

        // Compute scales per group
        Object.keys(groups).forEach(function(grp) {
          var g = groups[grp];
          var xMin;
          if (me.historyFilterSecsAgo) {
            xMin = now - me.historyFilterSecsAgo;
          } else {
            // "Max" mode — find earliest data point within this group
            xMin = now;
            g.servers.forEach(function(s) {
              if (s.monitorData && s.monitorData.bins && s.monitorData.bins.length > 0) {
                if (s.monitorData.bins[0].time < xMin) xMin = s.monitorData.bins[0].time;
              }
            });
          }
          g.xMin = xMin;

          g.servers.forEach(function(s) {
            if (!s.monitorData || !s.monitorData.bins) return;
            s.monitorData.bins.forEach(function(d) {
              if (d.time < xMin) return;
              if (d.successpersecond > g.maxReqs) g.maxReqs = d.successpersecond;
              if (d.totalerrors > g.maxErrors) g.maxErrors = d.totalerrors;
              if (d.meanExecTime > 0) g.execTimeValues.push(d.meanExecTime);
              if (d.p99ExecTime > 0) g.execTimeValues.push(d.p99ExecTime);
              if (d.meanTotalExecTime > 0) g.execTimeValues.push(d.meanTotalExecTime);
            });
          });

          // 95th percentile for exec time
          var maxExecTime = 1;
          if (g.execTimeValues.length > 0) {
            g.execTimeValues.sort(function(a, b) { return a - b; });
            var p95Index = Math.floor(g.execTimeValues.length * 0.95);
            maxExecTime = g.execTimeValues[Math.min(p95Index, g.execTimeValues.length - 1)];
            if (maxExecTime < 10) maxExecTime = 10;
            maxExecTime = maxExecTime * 1.2;
          }
          g.maxExecTime = maxExecTime;
        });

        // Store per-group scales keyed by group name
        me._groupScales = {};
        Object.keys(groups).forEach(function(grp) {
          var g = groups[grp];
          me._groupScales[grp] = { maxReqs: g.maxReqs, maxErrors: g.maxErrors, maxExecTime: g.maxExecTime, xMin: g.xMin, xMax: now };
        });

        me.servers.forEach(function(s) {
          if (s.monitorData && s.monitorData.bins) me.refreshHistoryChart(s);
        });
      },
  },

  computed:{
    // ... all your existing computed properties stay the same
    isAdmin() {
      let me=this;
      return me.sessionState && me.sessionState.contextProperties &&  me.sessionState.contextProperties.isAdmin==="true";
    },

    expandedChart() {
      // Expand chart to full width for time windows > 3 hours (or "Max")
      return !this.historyFilterSecsAgo || this.historyFilterSecsAgo > 10800;
    },

    selectedServerJobs() {
      var me=this;
      if (!me.selectedServer || !me.selectedServer.monitorData) return [];
      return me.selectedServer.monitorData.jobs;
    },

    filteredServerJobs() {
      var me=this;
      if (!me.selectedServerJobs) return [];
      let jobs=me.selectedServerJobs;
      if (me.hideSystemJobs) jobs=jobs.filter(function(j){return j.pool!=="System";});
      return jobs.slice(0,me.maxJobDisplayCount);
    },

    detailedServerStatus() {
      var me=this;
      if (!me.selectedServer || !me.selectedServer.statusDetailed) return null;
      let props=Object.keys(me.selectedServer.statusDetailed);
      props.sort();
      return props.reduce(function(a, d) {
            a.push({name:d, value:me.selectedServer.statusDetailed[d]})
            return a;
      }, []);
    },

    logErrors() {
      return this.countLogByLevel('ERROR');
    },

    logWarnings() {
       return this.countLogByLevel('WARN');
    },

    logInfos() {
       return this.countLogByLevel('INFO');
    },

    filteredServerLog() {
      var me=this;
      var serverLog=me.serverLog;
      if (!serverLog) return [];
      return serverLog.filter(function(d) {
        if (!d[2]) return false;
        if (me.logFilter && !d[2].toLowerCase().includes(me.logFilter.toLowerCase())) return false;
        if (me.logLevels && me.logLevels.length>0 && !me.logLevels.includes(d[1])) return false;
        return true;
      });
    }
  },

  mounted() {
    var me=this;
   
    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeyDown);

    var vcCommunicator=window.vcCommunicator;
    if (!vcCommunicator) {
      console.log("No vcCommunicator");
      return;
    }

    vcCommunicator.waitForSession(function (sessionState, error) {
      if (error) {
        console.log(`Failed to attach session... ${error}`);
      }
      me.sessionState=sessionState;
      me.refreshMonitor();
      me.waitingForSessionAttempt=false;
    });

    vcCommunicator.waitForTerminateSession(function () {
      me.sessionState=null;
    });
  },

  beforeUnmount() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeyDown);
  },

  components: {
    CommonModals
  }
}
</script>

<style scoped>
/* Modal styles to ensure proper display without Bootstrap JS */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}

.modal-backdrop.show {
  opacity: 0.5;
}

.modal-dialog {
  position: relative;
  z-index: 1060;
}

/* ---- Chart container ---- */
.chart-container {
  position: relative;
}
.chart-container .activitychart {
  height: 100px;
}
.chart-container.chart-expanded .activitychart {
  height: 180px;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 12px;
  font-size: 10px;
  color: #666;
  padding: 2px 0 0;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.legend-swatch {
  display: inline-block;
  width: 10px;
  height: 3px;
  border-radius: 1px;
}
.legend-success { background: #1a961a; }
.legend-exectime { background: #555; }
.legend-p99exectime { background: #9467bd; }
.legend-totalexectime { background: #3366cc; }
.legend-unhandled { background: #d73027; height: 8px; width: 8px; border-radius: 0; }
.legend-handled { background: #e8870e; height: 8px; width: 8px; border-radius: 0; }
.legend-license { background: #e8d30e; height: 8px; width: 8px; border-radius: 0; }

/* Tooltip */
.chart-tooltip {
  position: absolute;
  background: rgba(255,255,255,0.95);
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 11px;
  line-height: 1.4;
  pointer-events: none;
  z-index: 10;
  box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  min-width: 110px;
}

.activitychart {
    background-color: #fff;
}

/* Grid lines */
.activitychart :deep() .grid-lines line {
    stroke: #e8e8e8;
    stroke-dasharray: 2,2;
}
.activitychart :deep() .grid-lines .tick text { display:none; }

/* Hover crosshair */
.activitychart :deep() .hover-line {
    stroke: #999;
    stroke-width: 1;
    stroke-dasharray: 3,3;
}

/* Success line & area */
.activitychart :deep() .success {
    stroke: #1a961a;
    stroke-width: 1.5;
    fill: none;
}
.activitychart :deep() .success-area {
    fill: rgba(26, 150, 26, 0.08);
}

/* Exec time line (mean processing time) */
.activitychart :deep() .exectime {
    stroke: #555;
    stroke-width: 1.2;
    stroke-dasharray: 4,2;
    fill: none;
}

/* p99 Exec time line */
.activitychart :deep() .p99exectime {
    stroke: #9467bd;
    stroke-width: 1;
    stroke-dasharray: 2,2;
    fill: none;
}

/* Total exec time line (mean round-trip) */
.activitychart :deep() .totalexectime {
    stroke: #3366cc;
    stroke-width: 1.2;
    stroke-dasharray: 6,2;
    fill: none;
}

/* Error bars */
.activitychart :deep() .error {
    fill: rgb(128, 4, 0);
}
.activitychart :deep() .unexpectederror {
    stroke: rgb(128, 4, 0);
    fill: none;
}
.activitychart :deep() .licenseerror {
    stroke: rgb(243, 231, 65);
    fill: none;
}
.activitychart :deep() .handlederror {
    stroke: rgb(133, 82, 25);
    fill: none;
}

/* Server backoff/paused states */
.server-backoff {
  opacity: 0.6;
}
.server-paused {
  opacity: 0.45;
}
</style>